"use client";
import Link from "@/lib/spa/Link";
import { usePathname } from "@/lib/spa/navigation";
import { useEffect, useState, type ReactNode } from "react";
import {
  orderTabs,
  visibleTabs,
  useSubTabOrder,
  SUBTAB_DND_MIME as DND_MIME,
} from "@/lib/nav/subTabOrder";

export type SubTab = { href: string; label: string };

/**
 * Section sub-navigation. Pass `sticky` to pin the tab bar just below the
 * sticky TopNav (at its measured height, `--mpp-topnav-h`: one row is 49px,
 * and it wraps to two on a narrower window) so it stays visible while the page scrolls —
 * used by Market Timing, whose sub-pages are long, scrolling workspaces. The
 * pinned bar carries the page background (`bg-canvas`) + the section padding
 * so content scrolls cleanly underneath it; it sits below the TopNav (z-40)
 * and above page content but below modals/dropdowns (z-50).
 *
 * Pass `orderKey` to make the bar **drag-arrangeable**: grab a tab and drop it
 * on another to move it, persisted per section (see `lib/nav/subTabOrder.ts`)
 * and mirrored across browsers. The same order is editable from Settings →
 * Pages, so it's reachable both ways. Without `orderKey` the bar is a plain
 * static list — ordering is opt-in per section, not imposed on every sub-nav.
 */
export function SubTabs({
  tabs,
  sticky = false,
  orderKey,
  activeHref,
  compact = false,
  children,
}: {
  tabs: SubTab[];
  sticky?: boolean;
  /** Section id; enables drag-to-reorder + persistence when set. */
  orderKey?: string;
  /** The current tab when tabs differ by query rather than path (Options:
   *  `/options?tab=flow`). Defaults to the pathname. */
  activeHref?: string;
  /** Tighter bottom margin, for a bar that also carries page controls. */
  compact?: boolean;
  /** Page controls on the same pinned row, after the tabs. */
  children?: ReactNode;
}) {
  const routePath = usePathname();
  const pathname = activeHref ?? routePath;
  const hydrate = useSubTabOrder((s) => s.hydrate);
  const setOrder = useSubTabOrder((s) => s.set);
  const resetOrder = useSubTabOrder((s) => s.reset);
  // Subscribe to just this section's order. Starts empty on the server AND on
  // the first client render (the store's initial state), so the shipped order
  // renders identically on both — the saved order lands after `hydrate`, which
  // keeps this out of hydration-mismatch territory.
  const saved = useSubTabOrder((s) => (orderKey ? s.orders[orderKey] : undefined));
  const hidden = useSubTabOrder((s) => (orderKey ? s.hidden[orderKey] : undefined));

  useEffect(() => {
    if (orderKey) hydrate(orderKey);
  }, [orderKey, hydrate]);

  const [dragHref, setDragHref] = useState<string | null>(null);
  const [overHref, setOverHref] = useState<string | null>(null);

  // Hidden tabs drop out of the bar — EXCEPT the one you're currently on, so
  // hiding the page you're standing on doesn't strand you with no active tab.
  const shown = orderKey
    ? visibleTabs(orderTabs(tabs, saved ?? []), (hidden ?? []).filter((h) => h !== pathname))
    : tabs;
  const ordered = shown;
  const customized = !!orderKey && (saved?.length ?? 0) > 0;

  const clearDnd = () => {
    setDragHref(null);
    setOverHref(null);
  };

  const drop = (targetHref: string, sourceHref: string | null) => {
    const from0 = sourceHref ?? dragHref;
    if (!orderKey || !from0 || from0 === targetHref) return clearDnd();
    const hrefs = ordered.map((t) => t.href);
    const from = hrefs.indexOf(from0);
    const to = hrefs.indexOf(targetHref);
    if (from < 0 || to < 0) return clearDnd();
    hrefs.splice(to, 0, ...hrefs.splice(from, 1));
    setOrder(orderKey, hrefs);
    clearDnd();
  };

  return (
    <nav
      className={`mpp-subtabs ${compact ? "mb-2" : "mb-6"} flex flex-wrap items-center gap-1 border-b border-line pb-2 ${
        sticky
          ? "sticky z-30 -mx-3 bg-canvas px-3 pt-3 sm:-mx-6 sm:px-6 xl:-mx-8 xl:px-8 2xl:-mx-10 2xl:px-10"
          : ""
      }`}
      style={sticky ? { top: "calc(var(--mpp-topnav-h) + var(--mpp-sat))" } : undefined}
    >
      {ordered.map((t, i) => {
        const active = pathname === t.href;
        const isOver = overHref === t.href && dragHref !== null && dragHref !== t.href;
        return (
          <Link
            key={t.href}
            href={t.href}
            prefetch
            draggable={!!orderKey}
            onDragStart={
              orderKey
                ? (e) => {
                    setDragHref(t.href);
                    e.dataTransfer.effectAllowed = "move";
                    // Carry the source in the payload rather than relying on
                    // the `dragHref` state: dragstart's setState hasn't
                    // re-rendered by the time the first dragover/drop closure
                    // runs, so a state-only handler misses drops that arrive in
                    // the same tick. The payload is readable immediately.
                    e.dataTransfer.setData(DND_MIME, t.href);
                    // An <a> natively drags its URL; blank the text payload so
                    // dropping on another app (or the address bar) can't turn a
                    // reorder into a navigation.
                    e.dataTransfer.setData("text/plain", "");
                  }
                : undefined
            }
            onDragOver={
              orderKey
                ? (e) => {
                    // Gate on the payload TYPE (available during dragover, when
                    // reading its value is not) so an unrelated drag — a file, a
                    // link from another app — is never treated as a reorder.
                    if (!e.dataTransfer.types.includes(DND_MIME)) return;
                    e.preventDefault();
                    e.dataTransfer.dropEffect = "move";
                    setOverHref(t.href);
                  }
                : undefined
            }
            onDrop={
              orderKey
                ? (e) => {
                    if (!e.dataTransfer.types.includes(DND_MIME)) return;
                    e.preventDefault();
                    drop(t.href, e.dataTransfer.getData(DND_MIME) || null);
                  }
                : undefined
            }
            onDragEnd={orderKey ? clearDnd : undefined}
            title={orderKey ? `${t.label} — drag to reorder` : undefined}
            className={`rounded-md px-3 py-1.5 text-xs font-medium transition ${
              active ? "bg-accent text-on-accent shadow-sm" : "text-ink-muted hover:bg-accent-soft hover:text-accent-ink"
            } ${dragHref === t.href ? "opacity-40" : ""} ${
              isOver ? "ring-1 ring-inset ring-accent" : ""
            }`}
          >
            {/* On the active tab the number sits on the accent fill: the faint ink
                it wears elsewhere has the fill's own lightness and vanished there. */}
            <span className={`mr-1.5 text-2xs ${active ? "text-on-accent/80" : "text-ink-faint"}`}>{i + 1}.</span>
            {t.label}
          </Link>
        );
      })}
      {customized && (
        <button
          type="button"
          onClick={() => orderKey && resetOrder(orderKey)}
          title="Restore the shipped tab order"
          className="ml-2 rounded-md px-2 py-1.5 text-2xs font-medium text-ink-faint transition hover:text-ink"
        >
          Reset order
        </button>
      )}
      {children}
    </nav>
  );
}
